import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { ACTION_SHORTCUT_TRIGGERED, VIDEO_MUTE, createShortcutEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { translate } from '../../../base/i18n/functions';
import { IGUMPendingState } from '../../../base/media/types';
import AbstractButton from '../../../base/toolbox/components/AbstractButton';
import Spinner from '../../../base/ui/components/web/Spinner';
import { registerShortcut, unregisterShortcut } from '../../../keyboard-shortcuts/actions';
import { SPINNER_COLOR } from '../../constants';
import AbstractVideoMuteButton, { mapStateToProps as abstractMapStateToProps } from '../AbstractVideoMuteButton';
import Icon from '../../../base/icons/components/Icon';
import { IconVideoBan, IconWarningCircleFill } from '../../../base/icons/svg';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { getLocalParticipant, getParticipantByIdOrUndefined } from '../../../base/participants';
import { isForceMuted } from '../../../participants-pane/functions';
import { MEDIA_TYPE } from '../../../base/media/constants';
const styles = () => {
    return {
        pendingContainer: {
            position: 'absolute',
            bottom: '1px',
            right: '9px'
        },
        pendingContainerWithLabel: {
            position: 'absolute',
            bottom: '17px',
            right: '0px'
        },
    };
};
/**
 * Component that renders a toolbar button for toggling video mute.
 *
 * @augments AbstractVideoMuteButton
 */
class VideoMuteButton extends AbstractVideoMuteButton {
    /**
     * Initializes a new {@code VideoMuteButton} instance.
     *
     * @param {IProps} props - The read-only React {@code Component} props with
     * which the new instance is to be initialized.
     */
    constructor(props) {
        super(props);
        // Bind event handlers so they are only bound once per instance.
        this._onKeyboardShortcut = this._onKeyboardShortcut.bind(this);
        // this._getTooltip = this._getLabel;
    }
    /**
     * Registers the keyboard shortcut that toggles the video muting.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        this.props.dispatch(registerShortcut({
            character: 'V',
            helpDescription: 'keyboardShortcuts.videoMute',
            handler: this._onKeyboardShortcut
        }));
    }
    /**
     * Unregisters the keyboard shortcut that toggles the video muting.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        this.props.dispatch(unregisterShortcut('V'));
    }
    _getIcon() {
        return (this._isToggled() ? (this.props._isBan ? IconVideoBan : this.toggledIcon) : this.icon) || this.icon;
    }
    /**
     * Gets the current accessibility label, taking the toggled and GUM pending state into account. If no toggled label
     * is provided, the regular accessibility label will also be used in the toggled state.
     *
     * The accessibility label is not visible in the UI, it is meant to be used by assistive technologies, mainly screen
     * readers.
     *
     * @private
     * @returns {string}
     */
    _getAccessibilityLabel() {
        const { _gumPending } = this.props;
        if (_gumPending === IGUMPendingState.NONE) {
            return super._getAccessibilityLabel();
        }
        return 'toolbar.accessibilityLabel.videomuteGUMPending';
    }
    /**
     * Gets the current label, taking the toggled and GUM pending state into account. If no
     * toggled label is provided, the regular label will also be used in the toggled state.
     *
     * @private
     * @returns {string}
     */
    _getLabel() {
        const { _gumPending } = this.props;
        if (_gumPending === IGUMPendingState.NONE) {
            return super._getLabel();
        }
        return 'toolbar.videomuteGUMPending';
    }
    /**
     * Indicates if video is currently muted or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isVideoMuted() {
        if (this.props._gumPending === IGUMPendingState.PENDING_UNMUTE) {
            return false;
        }
        return super._isVideoMuted();
    }
    /**
     * Returns a spinner if there is pending GUM.
     *
     * @returns {ReactElement | null}
     */
    _getElementAfter() {
        const { _gumPending, showLabel, hasPermissions } = this.props;
        const classes = withStyles.getClasses(this.props);
        if (!hasPermissions) {
            return (React.createElement(Tooltip, { position: 'right', content: 'toolbar.accessibilityLabel.warningPermission.camera' },
                React.createElement("div", { className: showLabel ? classes.pendingContainerWithLabel : classes.pendingContainer },
                    React.createElement(Icon, { size: 17, src: IconWarningCircleFill }))));
        }
        return _gumPending === IGUMPendingState.NONE ? null
            : (React.createElement("div", { className: showLabel ? classes.pendingContainerWithLabel : classes.pendingContainer },
                React.createElement(Spinner, { color: SPINNER_COLOR, size: 'small' })));
    }
    /**
     * Creates an analytics keyboard shortcut event and dispatches an action to
     * toggle the video muting.
     *
     * @private
     * @returns {void}
     */
    _onKeyboardShortcut() {
        // Ignore keyboard shortcuts if the video button is disabled.
        if (this._isDisabled()) {
            return;
        }
        sendAnalytics(createShortcutEvent(VIDEO_MUTE, ACTION_SHORTCUT_TRIGGERED, { enable: !this._isVideoMuted() }));
        AbstractButton.prototype._onClick.call(this);
    }
}
/**
 * Default values for {@code VideoMuteButton} component's properties.
 *
 * @static
 */
VideoMuteButton.defaultProps = {
    ...AbstractVideoMuteButton.defaultProps,
    hasPermissions: true,
};
/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code VideoMuteButton} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _videoMuted: boolean
 * }}
 */
function _mapStateToProps(state) {
    const { gumPending } = state['features/base/media'].video;
    const localParticipantId = getLocalParticipant(state)?.id;
    const participant = getParticipantByIdOrUndefined(state, localParticipantId);
    const isForceMute = isForceMuted(participant, MEDIA_TYPE.VIDEO, state);
    return {
        ...abstractMapStateToProps(state),
        _gumPending: gumPending,
        _isBan: isForceMute
    };
}
export default withStyles(translate(connect(_mapStateToProps)(VideoMuteButton)), styles);
